/**
 * Observability — Single entry point.
 *
 * Architecture:
 *   Grafana → logs, web vitals, RUM, OTel traces, dashboards, errors
 *   Logger  → routes to Grafana (logs + errors) automatically
 *
 * Usage:
 *   // In main.tsx (once):
 *   import { initObservability } from "@/modules/shared/lib/observability";
 *   initObservability();
 *
 *   // Anywhere in the app:
 *   import { log, captureError, setObservabilityUser } from "@/modules/shared/lib/observability";
 *   log.info("Something happened", { key: "value" });
 *   log.error("Failed", { dealId }, error);
 */

import { initGrafana, setGrafanaUser, clearGrafanaUser, captureError } from "./grafana";
import { initSentry, setSentryUser, clearSentryUser, captureSentryError } from "./sentry";
import { observabilityConfig } from "./env";

/** Initialize all observability tools. Call once in main.tsx. */
export function initObservability(): void {
  initSentry();
  initGrafana();

  // Always forward uncaught errors and rejections to the API → CloudWatch
  window.addEventListener("error", (event) => {
    sendClientLog("error", event.message || "window error", event.error, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    sendClientLog("error", "unhandledrejection", reason instanceof Error ? reason : new Error(String(reason)));
  });

  if (!observabilityConfig.isProduction) {
    console.log(
      `%c[observability]%c Grafana: ${observabilityConfig.grafana.enabled ? "ON" : "OFF"} | Sentry: ${observabilityConfig.sentry.enabled ? "ON" : "OFF"} | env: ${observabilityConfig.environment}`,
      "color:#4964A2;font-weight:bold",
      "color:inherit",
    );
  }
}

/** Set user context on Grafana + Sentry. Call on login. */
export function setObservabilityUser(user: { id: string; email?: string }): void {
  setGrafanaUser({ id: user.id }); // Only send ID, never email
  setSentryUser({ id: user.id });
}

/** Clear user context. Call on logout. */
export function clearObservabilityUser(): void {
  clearGrafanaUser();
  clearSentryUser();
}

/**
 * Report a caught error to all observability systems.
 *
 * Sends to Sentry (issue + Slack alert) and Grafana Faro (dashboard).
 * Context keys "operation", "provider", "action", "function_name"
 * become searchable tags in Sentry.
 *
 * Usage:
 *   reportError(err, "pull-crm-deals", { provider: "hubspot", action: "pull-deals" });
 */
export function reportError(
  error: unknown,
  operation: string,
  context?: Record<string, string>,
): void {
  const errorObj = error instanceof Error ? error : new Error(String(error));
  const ctx = {
    operation,
    client_origin: window.location.hostname,
    ...context,
  };
  captureError(errorObj, ctx);
  captureSentryError(errorObj, ctx);
  sendClientLog("error", `[${operation}] ${errorObj.message}`, errorObj, ctx);
  if (import.meta.env.DEV) {
    console.error(`[reportError:${operation}]`, error, context);
  }
}

/**
 * Ship a log entry to the API → CloudWatch.
 *
 * Fire-and-forget. Uses sendBeacon when available so logs survive
 * page navigation/unload.
 */
function sendClientLog(
  level: "debug" | "info" | "warn" | "error",
  message: string,
  error?: unknown,
  context?: Record<string, unknown>,
): void {
  try {
    const apiUrl = import.meta.env.VITE_API_URL;
    if (!apiUrl) return;
    const errObj = error instanceof Error ? error : undefined;
    const payload = {
      level,
      message,
      error: errObj
        ? { name: errObj.name, message: errObj.message, stack: errObj.stack }
        : undefined,
      context,
      url: window.location.href,
      userAgent: navigator.userAgent,
    };
    const body = JSON.stringify(payload);
    const url = `${apiUrl}/logs/client`;
    if (typeof navigator.sendBeacon === "function") {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(url, blob);
    } else {
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    // Swallow — never let logging crash the app
  }
}

// Re-exports
export { log } from "./logger";
export { captureError, pushEvent, getFaro } from "./grafana";
export { captureSentryError, sentryLogger } from "./sentry";
export { observabilityConfig } from "./env";
export { ObservabilityErrorBoundary, SentryErrorBoundary } from "./errorBoundary";
